import { Logger } from '../Logger/Logger';

type TEventHandler = {
  event: any;
  once?: boolean;
  fn(): void;
};

export const matchesHandler = (handler, event, fn) => handler.event === event && (handler.fn === fn || !fn);

export class WindowMessenger {
  private targetWindow: any;
  private handlers: TEventHandler[] = [];
  private logger: Logger;

  constructor(targetWindow, logger: Logger = new Logger('WindowMessenger')) {
    if (!targetWindow) {
      throw new Error('target window should be defined');
    }

    this.logger = logger;
    this.targetWindow = targetWindow;

    window.addEventListener('message', this.handleMessage, false);
  }

  on(event, fn) {
    this.handlers.push({
      event,
      fn,
    });
  }

  one(event, fn) {
    this.handlers.push({
      event,
      fn,
      once: true,
    });
  }

  off(event, fn) {
    this.handlers = this.handlers.filter((observer) => !matchesHandler(observer, event, fn));
  }

  emit(event, data = null, transferable = null) {
    this.logger.log('postMessage emitted', event, data);
    if (transferable) {
      this.targetWindow.postMessage({ data, event }, '*', transferable);
    } else {
      this.targetWindow.postMessage({ data, event }, '*');
    }
  }

  private runObserver = (event, data) => (observer) => {
    observer.fn.call(event, data);

    if (observer.once) {
      this.off(observer.event, observer.fn);
    }
  };

  private parseMessageData(input: any) {
    if (typeof input !== 'string') {
      return input;
    }

    try {
      return JSON.parse(input);
    } catch (ex) {
      return null;
    }
  }

  private handleMessage = (event) => {
    if (event.source !== this.targetWindow) {
      return;
    }

    const data = this.parseMessageData(event.data);
    if (data === null) {
      return;
    }

    this.logger.log('postMessage received', data);

    this.handlers.filter((observer) => observer.event === data.event).forEach(this.runObserver(event, data.data));
  };
}
